/**
 * StartScreenManager - Handles start screen UI and interactions
 *
 * Responsibilities:
 * - Start button setup and click handling
 * - New game button and confirmation modal
 * - Settings button and modal
 * - Account button handling
 * - Music toggle button
 * - Cheat code listener
 * - Loading progress tracking
 */

import { ModalManager } from './ModalManager.js';
import { isConfigured as firebaseConfigured } from '../firebase/config.js';

export class StartScreenManager {
    constructor() {
        // DOM elements
        this.startButton = document.getElementById('start-button');
        this.startButtonText = document.getElementById('start-button-text');
        this.startButtonProgress = document.getElementById('start-button-progress');
        this.startScreen = document.getElementById('start-screen');
        this.offlineDisplay = document.getElementById('offline-progress-display');

        // State
        this.loadingComplete = false;
        this.cheatCode = '';
        this.targetCode = 'iddqd';

        // Modal manager for start screen modals
        this.modalManager = new ModalManager();

        // Expose for session invalidation callbacks
        window.startScreenModalManager = this.modalManager;
    }

    /**
     * Initialize the start screen
     * @param {Function} initAppCallback - Function to call to initialize the app
     */
    initialize(initAppCallback) {
        // Check if user has any save data (for UI display purposes)
        const hasSimulatorState = localStorage.getItem('offlineSimulatorState_roadTripSave_anonymous') !== null ||
                                  localStorage.getItem('offlineSimulatorState_roadTripSave_account') !== null;
        const hasSaveGame = localStorage.getItem('roadtripGameState') !== null ||
                            localStorage.getItem('roadTripSave_anonymous') !== null ||
                            localStorage.getItem('roadTripSave_account') !== null;

        const hasExistingData = hasSimulatorState || hasSaveGame;

        console.log('[StartScreen] Initialize:', { hasSimulatorState, hasSaveGame });

        if (hasExistingData) {
            // Show offline display (will be populated after app init)
            if (this.offlineDisplay) {
                this.offlineDisplay.classList.add('active');
            }
            this.startButtonText.textContent = 'DRIVE';
        }

        // Set up all event listeners
        this._setupCheatCodeListener();
        this._setupLoadingProgress(hasExistingData);
        this._setupButtonHandlers();
        this._setupStartButton();

        // Start initialization in background
        initAppCallback();
    }

    /**
     * Check if loading is complete
     */
    isReady() {
        return this.loadingComplete || this.startButton.classList.contains('ready');
    }

    /**
     * Set up cheat code listener (IDDQD)
     */
    _setupCheatCodeListener() {
        document.addEventListener('keydown', (e) => {
            // Don't capture if typing in an input or textarea
            const tag = e.target.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;

            // Don't capture if a modal overlay is present
            if (document.querySelector('.cf-modal-overlay')) return;

            // Only listen when start screen is visible
            if (!this.startScreen.classList.contains('hidden')) {
                this.cheatCode += e.key.toLowerCase();
                // Keep only last 5 characters
                if (this.cheatCode.length > 5) {
                    this.cheatCode = this.cheatCode.slice(-5);
                }

                if (this.cheatCode === this.targetCode) {
                    this.cheatCode = ''; // Reset

                    // Prompt for miles
                    const totalMiles = prompt('Enter total miles (lifetime miles earned):');
                    const currentMiles = prompt('Enter current miles (spendable currency):');

                    if (totalMiles !== null && currentMiles !== null) {
                        const total = parseInt(totalMiles) || 0;
                        const current = parseInt(currentMiles) || 0;

                        // Create or update save game
                        const saveData = {
                            miles: current,
                            totalMiles: total,
                            currentCarId: 'starter',
                            cars: {
                                starter: {
                                    unlocked: true,
                                    upgradeLevels: {}
                                }
                            },
                            migrationVersion: 2 // Skip migration
                        };

                        localStorage.setItem('roadtripGameState', JSON.stringify(saveData));
                        alert(`Save game created!\nTotal Miles: ${total}\nCurrent Miles: ${current}\n\nReload the page to apply.`);
                    }
                }
            }
        });
    }

    /**
     * Set up loading progress handler
     */
    _setupLoadingProgress(hasExistingData) {
        window.updateLoadingProgress = (percent) => {
            this.startButtonProgress.style.width = percent + '%';
            if (percent >= 100 && !this.loadingComplete) {
                this.loadingComplete = true;
                this.startButtonText.textContent = hasExistingData ? 'DRIVE' : 'START';
                this.startButton.classList.add('ready');

                // Show secondary buttons container
                const secondaryButtons = document.getElementById('secondary-buttons');
                if (secondaryButtons) {
                    secondaryButtons.classList.add('ready');
                }

                // Show NEW GAME button only if there's existing data
                const newGameButton = document.getElementById('new-game-button');
                if (hasExistingData && newGameButton) {
                    newGameButton.classList.add('ready');
                }

                // Always show settings button when loaded
                const settingsButton = document.getElementById('settings-button');
                if (settingsButton) {
                    settingsButton.classList.add('ready');
                }

                // Show account button when Firebase is configured
                const accountButton = document.getElementById('account-button');
                if (accountButton && firebaseConfigured) {
                    accountButton.classList.add('ready');
                    // Update visual state based on sign-in status
                    const app = window.roadTripApp;
                    if (app?.accountManager?.isLinked()) {
                        accountButton.classList.add('signed-in');
                    }
                }

                // Start menu loop music on first load (if YouTube hasn't been used)
                const app = window.roadTripApp;
                if (app?.menuLoopPlayer) {
                    app.menuLoopPlayer.play(1500); // 1.5s fade-in
                }
            }
        };
    }

    /**
     * Set up button handlers (new game, settings, account, music)
     */
    _setupButtonHandlers() {
        // New Game button
        const newGameButton = document.getElementById('new-game-button');
        if (newGameButton) {
            newGameButton.addEventListener('click', () => {
                if (this.isReady()) {
                    this._showNewGameConfirmModal();
                }
            });
        }

        // Settings button
        const settingsButton = document.getElementById('settings-button');
        if (settingsButton) {
            settingsButton.addEventListener('click', () => {
                if (this.isReady()) {
                    this._showSettingsModal();
                }
            });
        }

        // Account button
        const accountButton = document.getElementById('account-button');
        if (accountButton) {
            accountButton.addEventListener('click', () => {
                if (this.isReady()) {
                    this._showAccountModal();
                }
            });
        }

        // Music toggle button
        const musicToggleButton = document.getElementById('music-toggle-button');
        if (musicToggleButton) {
            musicToggleButton.addEventListener('click', () => {
                const app = window.roadTripApp;
                if (app?.youtubePlayer) {
                    const isPlaying = app.youtubePlayer.toggle();
                    musicToggleButton.classList.toggle('playing', isPlaying);
                    musicToggleButton.title = isPlaying ? 'Pause Music' : 'Play Music';

                    // When YouTube starts, stop menu loop permanently for this session
                    if (isPlaying && app.menuLoopPlayer) {
                        app.menuLoopPlayer.stop(true); // true = mark YouTube as having played
                    }

                    // Sync with game UI button if it exists
                    const gameBtn = document.getElementById('game-music-toggle');
                    if (gameBtn) {
                        gameBtn.classList.toggle('playing', isPlaying);
                        gameBtn.title = isPlaying ? 'Pause Music' : 'Play Music';
                    }
                }
            });
        }
    }

    /**
     * Set up start button click handler
     */
    _setupStartButton() {
        this.startButton.addEventListener('click', async () => {
            if (this.isReady()) {
                const app = window.roadTripApp;

                // Check if simulator is running, suspended, or has pending progress
                const simulatorActive = app?.offlineSimulator?.isRunning || app?.offlineSimulator?.isSuspended;
                const slotKey = app?.gameState?.saveManager?.getActiveSaveKey() || 'roadTripSave_anonymous';
                const hasPendingSimulatorState = localStorage.getItem(`offlineSimulatorState_${slotKey}`) !== null;

                if (app && (simulatorActive || hasPendingSimulatorState)) {
                    // Reload game - applies simulator progress
                    app.reloadGame();
                    return;
                }

                // Hide start screen with fade
                this.startScreen.style.transition = 'opacity 1s ease-out';
                this.startScreen.style.opacity = '0';
                setTimeout(() => {
                    this.startScreen.classList.add('hidden');
                    // Trigger the actual game start
                    if (window.startGame) {
                        window.startGame();
                    }
                }, 1000);
            }
        });
    }

    /**
     * Show new game confirmation modal
     */
    _showNewGameConfirmModal() {
        // Create overlay
        const overlay = document.createElement('div');
        overlay.className = 'cf-ui';
        overlay.style.cssText = `
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.85);
            backdrop-filter: blur(4px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10000;
        `;

        // Create modal container
        const modal = document.createElement('div');
        modal.style.cssText = `
            background: var(--cf-bg-primary);
            border: 1px solid var(--cf-divider);
            padding: var(--cf-space-xl);
            max-width: 340px;
            width: 90%;
            font-family: var(--cf-font-mono);
        `;

        // Build modal content with DOM
        const wrapper = document.createElement('div');
        wrapper.style.textAlign = 'center';

        const title = document.createElement('h2');
        title.className = 'cf-title';
        title.style.cssText = 'font-size: 16px; margin-bottom: var(--cf-space-md); color: var(--cf-text-primary);';
        title.textContent = 'START NEW GAME?';
        wrapper.appendChild(title);

        const message = document.createElement('p');
        message.className = 'cf-muted';
        message.style.cssText = 'margin-bottom: var(--cf-space-lg); font-size: var(--cf-font-size-sm); line-height: 1.5;';
        message.textContent = 'This will delete your current save and start fresh at 0 miles.';
        wrapper.appendChild(message);

        const btnContainer = document.createElement('div');
        btnContainer.style.cssText = 'display: flex; gap: var(--cf-space-md); justify-content: center;';

        const yesBtn = document.createElement('button');
        yesBtn.className = 'cf-button confirm-yes';
        yesBtn.style.cssText = 'background: rgba(180, 60, 60, 0.8); border-color: rgba(220, 80, 80, 0.6); color: rgba(255, 200, 200, 1);';
        yesBtn.textContent = 'DELETE SAVE';
        btnContainer.appendChild(yesBtn);

        const noBtn = document.createElement('button');
        noBtn.className = 'cf-button confirm-no';
        noBtn.textContent = 'CANCEL';
        btnContainer.appendChild(noBtn);

        wrapper.appendChild(btnContainer);
        modal.appendChild(wrapper);

        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        const closeModal = () => {
            overlay.remove();
        };

        yesBtn.onclick = async () => {
            closeModal();

            const app = window.roadTripApp;
            const isLoggedIn = app?.accountManager?.isLinked();

            // Stop any running offline simulator
            if (app?.offlineSimulator) {
                app.offlineSimulator.stop();
            }

            // Prevent beforeunload from re-saving the game
            window._skipSaveOnUnload = true;

            if (isLoggedIn) {
                // Logged in: Create fresh state, push to cloud, then save locally
                // This prevents race conditions where old cloud data might sync on reload
                const userId = app.accountManager.getUserId();
                const freshState = app.gameState.createInitialState();
                freshState._cloudSaveOwnerId = userId;

                // Push fresh state to cloud FIRST (overwrites any existing cloud save)
                try {
                    await app.cloudSaveManager.forceLocalToCloud(freshState);
                    console.log('[NewGame] Fresh state pushed to cloud');
                } catch (err) {
                    console.warn('[NewGame] Failed to push fresh state to cloud:', err);
                }

                // Now clear local storage (fresh state will be created on reload)
                localStorage.removeItem('roadTripSave_account');
                localStorage.removeItem('offlineSimulatorState_roadTripSave_account');
            } else {
                // Not logged in: Delete anonymous save slot only
                localStorage.removeItem('roadTripSave_anonymous');
                localStorage.removeItem('offlineSimulatorState_roadTripSave_anonymous');
            }

            // Also remove legacy key if present
            localStorage.removeItem('roadtripGameState');
            localStorage.removeItem('roadtripDebugLog');

            // Reload page to start fresh
            location.reload();
        };

        noBtn.onclick = closeModal;

        // Close on backdrop click
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                closeModal();
            }
        });

        // Close on Escape key
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                closeModal();
                document.removeEventListener('keydown', handleEscape);
            }
        };
        document.addEventListener('keydown', handleEscape);

        // Focus the cancel button (safer default)
        noBtn.focus();
    }

    /**
     * Show settings modal
     */
    _showSettingsModal() {
        const app = window.roadTripApp;

        // Create cassette-future styled modal
        const overlay = document.createElement('div');
        overlay.className = 'cf-ui cf-modal-overlay';

        const modal = document.createElement('div');
        modal.className = 'cf-modal';
        modal.style.maxWidth = '320px';

        // Header
        const header = document.createElement('div');
        header.className = 'cf-modal-header';
        const headerTitle = document.createElement('h2');
        headerTitle.className = 'cf-modal-title';
        headerTitle.textContent = 'SETTINGS';
        header.appendChild(headerTitle);
        const closeBtn = document.createElement('button');
        closeBtn.className = 'cf-modal-close';
        closeBtn.textContent = '\u00D7';
        header.appendChild(closeBtn);
        modal.appendChild(header);

        // Body
        const body = document.createElement('div');
        body.className = 'cf-modal-body';

        // Get current volume levels
        const sfxVolume = app?.gameState?.getSfxVolume() ?? 100;
        const musicVolume = app?.gameState?.getMusicVolume() ?? 50;

        // Helper to create slider rows
        const createSliderRow = (labelText, sliderId, value) => {
            const row = document.createElement('div');
            row.className = 'cf-slider-row';
            const label = document.createElement('span');
            label.className = 'cf-slider-label';
            label.textContent = labelText;
            row.appendChild(label);
            const slider = document.createElement('input');
            slider.type = 'range';
            slider.id = sliderId;
            slider.min = '0';
            slider.max = '100';
            slider.value = value;
            slider.className = 'cf-slider';
            row.appendChild(slider);
            return row;
        };

        body.appendChild(createSliderRow('Sound Effects', 'cf-sfx-slider', sfxVolume));
        body.appendChild(createSliderRow('Music', 'cf-music-slider', musicVolume));

        modal.appendChild(body);
        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        // Close function
        const closeModal = () => overlay.remove();

        // Close handlers
        closeBtn.addEventListener('click', closeModal);
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) closeModal();
        });

        // Escape key
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                closeModal();
                document.removeEventListener('keydown', handleEscape);
            }
        };
        document.addEventListener('keydown', handleEscape);

        // SFX volume slider handler
        body.querySelector('#cf-sfx-slider').addEventListener('input', (e) => {
            const volume = parseInt(e.target.value, 10);
            if (app?.sfxManager) {
                app.sfxManager.setVolume(volume);
            }
        });

        // Music volume slider handler
        body.querySelector('#cf-music-slider').addEventListener('input', (e) => {
            const volume = parseInt(e.target.value, 10);
            if (app?.gameState) {
                app.gameState.setMusicVolume(volume);
            }
            if (app?.youtubePlayer) {
                app.youtubePlayer.setVolume(volume);
                // Start playing if volume > 0 and not already playing
                if (volume > 0 && !app.youtubePlayer.isCurrentlyPlaying()) {
                    app.youtubePlayer.play();
                } else if (volume === 0) {
                    app.youtubePlayer.pause();
                }
            }
        });
    }

    /**
     * Show account modal
     */
    async _showAccountModal() {
        const app = window.roadTripApp;
        if (!app?.accountManager) return;

        const { showAccountPanel } = await import('./FirebaseUI.js');
        showAccountPanel({
            accountManager: app.accountManager,
            cloudSaveManager: app.cloudSaveManager,
            modalManager: this.modalManager,
            saveManager: app.gameState.saveManager
        });
    }
}

/**
 * Set up browser zoom prevention
 */
export function setupZoomPrevention() {
    // Prevent browser zoom via Ctrl/Cmd + scroll
    window.addEventListener('wheel', (e) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
        }
    }, { passive: false });

    // Prevent Ctrl/Cmd + plus/minus zoom
    window.addEventListener('keydown', (e) => {
        if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '=' || e.key === '0')) {
            e.preventDefault();
        }
    });

    // iOS Safari/Chrome: Prevent pinch-to-zoom via gesturestart/gesturechange
    // These events are iOS-specific and fire before touchmove when pinching
    document.addEventListener('gesturestart', (e) => {
        e.preventDefault();
    }, { passive: false });

    document.addEventListener('gesturechange', (e) => {
        e.preventDefault();
    }, { passive: false });

    document.addEventListener('gestureend', (e) => {
        e.preventDefault();
    }, { passive: false });

}
